import React, { useState } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Dropdown,
  message,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  AppstoreOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  useFetchCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from "./label.service";

const { Column } = Table;
const { Option } = Select;

export default function Categories() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState("");

  const { data: categoriesData, isLoading, refetch } = useFetchCategoriesQuery();
  const [createCategory, { isLoading: isCreateLoading }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdateLoading }] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const categories = categoriesData?.data?.categories || [];

  const handleAddCategory = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditCategory = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      usage: record.usage,
      color: record.color,
    });
    setIsModalVisible(true);
  };

  const handleDeleteCategory = async (id) => {
    Modal.confirm({
      title: "Delete Category",
      content: "Are you sure you want to delete this category?",
      onOk: async () => {
        setDeleteLoading(id);
        try {
          await deleteCategory(id).unwrap();
          message.success("Category deleted successfully");
          refetch();
        } catch (error) {
          message.error(error?.data?.message || "Failed to delete category");
        } finally {
          setDeleteLoading("");
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingCategory) {
        await updateCategory({ id: editingCategory._id, data: values }).unwrap();
        message.success("Category updated successfully");
      } else {
        await createCategory(values).unwrap();
        message.success("Category created successfully");
      }
      setIsModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      message.error(error?.data?.message || "Operation failed");
    }
  };

  const handleViewProducts = (categoryId) => {
    navigate(`/label/category/${categoryId}/products`);
  };

  const getUsageTag = (usage) => {
    const colors = {
      Prep: "blue",
      Storage: "green",
      Service: "orange",
    };
    return <Tag color={colors[usage]}>{usage}</Tag>;
  };

  const getMenuItems = (record) => [
    {
      key: "view",
      label: "View Products",
      icon: <EyeOutlined />,
      onClick: () => handleViewProducts(record._id),
    },
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      onClick: () => handleEditCategory(record),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDeleteCategory(record._id),
    },
  ];

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AppstoreOutlined />
          <span>Categories</span>
        </div>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>
          Add Category
        </Button>
      }
    >
      <Spin spinning={isLoading}>
        <Table
          dataSource={categories}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          rowClassName={(record) => !record.isActive ? "inactive-row" : ""}
        >
          <Column title="CATEGORY" dataIndex="name" />
          <Column title="FOR" dataIndex="usage" render={getUsageTag} />
          <Column
            title="USAGE"
            render={(_, record) => (
              <Space>
                <Tag>Devices: {record.deviceMapping?.length || 1}</Tag>
                <Tag color="blue">Products: {record.productCount || 0}</Tag>
              </Space>
            )}
          />
          <Column
            title="STATE"
            dataIndex="isActive"
            render={(active) => (
              <Tag color={active ? "green" : "red"}>{active ? "Active" : "Inactive"}</Tag>
            )}
          />
          <Column
            title="Actions"
            align="center"
            render={(_, record) => (
              <Space>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleViewProducts(record._id)}
                >
                  View Products
                </Button>
                <Dropdown menu={{ items: getMenuItems(record) }} placement="bottomRight">
                  <Button icon={<MoreOutlined />} size="small" />
                </Dropdown>
              </Space>
            )}
          />
        </Table>
      </Spin>

      <Modal
        title={editingCategory ? "Edit Category" : "Add Category"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={isCreateLoading || isUpdateLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: "Please enter category name" }]}
          >
            <Input placeholder="e.g., FISH, MEAT, VEGETABLES" />
          </Form.Item>
          <Form.Item name="usage" label="Usage" initialValue="Prep">
            <Select>
              <Option value="Prep">Prep</Option>
              <Option value="Storage">Storage</Option>
              <Option value="Service">Service</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Enter category description" />
          </Form.Item>
          <Form.Item name="color" label="Color">
            <Input type="color" style={{ width: "100%", height: "32px" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}